import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@/shared/lib/problem-details';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { getEntitlements } from '../api/billing.api';
import type { TenantEntitlements } from '../types/billing.types';
import { billingKeys } from './billing-keys';

/** Validade da leitura: a mesma janela do cache de direitos da API. */
export const ENTITLEMENTS_STALE_TIME_MS = 60_000;

const NO_FEATURES: ReadonlySet<string> = new Set<string>();

export interface UseTenantEntitlementsResult {
  /**
   * Os direitos da última leitura bem-sucedida, ou `undefined` enquanto
   * carrega e depois de uma falha. Quem precisa de "sem plano ativo" lê
   * `source === 'none'` daqui, nunca da falha: uma queda da rota não é o
   * tenant sem plano.
   */
  entitlements: TenantEntitlements | undefined;
  /** Os recursos do plano; vazio enquanto carrega e depois de uma falha. */
  features: ReadonlySet<string>;
  /** Se o plano dá o recurso. Área paga do painel decide por aqui, nunca pelo nome do plano. */
  hasFeature: (featureKey: string) => boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Direitos do tenant (`GET /billing/entitlements`), nome do contrato da
 * iniciativa do Atendente IA, consumido pelo `useCustomerAgentAvailable`.
 *
 * - Validade de 60 s; `billing.plan_changed` revalida na hora.
 * - Leitura que falha deixa o tenant **sem os recursos** até a próxima
 *   leitura bem-sucedida, sem quebrar as outras telas. Mesmo com dado
 *   anterior em cache: o recurso pago não fica ligado por uma leitura velha.
 */
export function useTenantEntitlements(options?: { enabled?: boolean }): UseTenantEntitlementsResult {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const query = useQuery<TenantEntitlements, ApiError>({
    queryKey: billingKeys.entitlements(tenantId),
    queryFn: getEntitlements,
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: ENTITLEMENTS_STALE_TIME_MS,
  });

  const entitlements = query.isError ? undefined : query.data;
  const features = useMemo<ReadonlySet<string>>(
    () => (entitlements ? new Set(entitlements.features) : NO_FEATURES),
    [entitlements],
  );

  const hasFeature = useCallback((featureKey: string) => features.has(featureKey), [features]);

  return {
    entitlements,
    features,
    hasFeature,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
